import { type TikTokCreatorInfo, lockSettingsToCreator } from '@shared/tiktok-creator-info';
import {
  DEFAULT_TIKTOK_POST_SETTINGS,
  type TikTokPostSettings,
  type TikTokPrivacyLevel,
} from '@shared/tiktok-post-params';
import {
  TIKTOK_BRANDED_PRIVATE_REASON,
  type TikTokMediaKind,
  buildTikTokCardView,
} from '@shared/tiktok-settings-view';

import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { FIELD_LABEL, SETTINGS_ROW_DESCRIPTION, SETTINGS_ROW_TITLE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface TikTokSettingsCardProps {
  mediaKind: TikTokMediaKind;
  /** What is stored on the post for TikTok; `null` until the creator first changes something. */
  value: TikTokPostSettings | null;
  onChange: (next: TikTokPostSettings) => void;
  creatorInfo: TikTokCreatorInfo | null;
  isRefreshing: boolean;
  /** `fresh` (just asked TikTok), `stale` (stored copy), `unavailable`, or null while loading. */
  infoState: string | null;
  /** How many photos the post has; the cover picker shows for two or more. */
  photoCount?: number;
  disabled?: boolean;
}

function Row({
  title,
  description,
  control,
  muted,
}: {
  title: string;
  description?: string | null;
  control: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-3', muted && 'opacity-60')}>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className={SETTINGS_ROW_TITLE}>{title}</span>
        {description ? <span className={SETTINGS_ROW_DESCRIPTION}>{description}</span> : null}
      </div>
      {control}
    </div>
  );
}

function CheckRow({
  id,
  label,
  checked,
  disabled,
  note,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  note?: string | null;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className={cn('flex items-start gap-2', disabled && 'opacity-60')}>
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onChange(next === true)}
        className="mt-0.5"
      />
      <label htmlFor={id} className="flex min-w-0 flex-col gap-0.5">
        <span className={SETTINGS_ROW_TITLE}>{label}</span>
        {note ? <span className={SETTINGS_ROW_DESCRIPTION}>{note}</span> : null}
      </label>
    </div>
  );
}

export function TikTokSettingsCard({
  mediaKind,
  value,
  onChange,
  creatorInfo,
  isRefreshing,
  infoState,
  photoCount = 1,
  disabled = false,
}: TikTokSettingsCardProps) {
  const settings = lockSettingsToCreator(value ?? DEFAULT_TIKTOK_POST_SETTINGS, creatorInfo);
  const view = buildTikTokCardView({ settings: value, creatorInfo, mediaKind });
  const toDrafts = settings.deliveryMode === 'drafts';
  const update = (patch: Partial<TikTokPostSettings>) => onChange({ ...settings, ...patch });

  const account = creatorInfo?.nickname
    ? `Posting as ${creatorInfo.nickname}`
    : isRefreshing
      ? 'Checking the TikTok account'
      : 'TikTok account';

  return (
    <section
      aria-label="TikTok settings"
      className="flex flex-col gap-3 rounded-lg border border-border/60 bg-card px-3 py-3"
    >
      <div className="flex flex-col gap-0.5">
        <span className={FIELD_LABEL}>TikTok settings</span>
        <span className={SETTINGS_ROW_DESCRIPTION}>{account}</span>
        {infoState === 'unavailable' || infoState === 'stale' ? (
          <span className={SETTINGS_ROW_DESCRIPTION}>
            {infoState === 'stale'
              ? 'TikTok could not be reached, so these options may be out of date.'
              : 'TikTok could not be reached, so only Only me is offered.'}
          </span>
        ) : null}
      </div>

      <div
        role="radiogroup"
        aria-label="How to send this post"
        className="grid grid-cols-2 gap-1.5"
      >
        {(
          [
            ['direct', 'Post to TikTok'],
            ['drafts', 'Send to TikTok drafts'],
          ] as const
        ).map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={settings.deliveryMode === mode}
            disabled={disabled}
            onClick={() => update({ deliveryMode: mode })}
            className={cn(
              'rounded-md border px-2.5 py-1.5 text-caption font-medium transition-colors',
              settings.deliveryMode === mode
                ? 'border-foreground/60 bg-muted text-foreground'
                : 'border-border text-muted-foreground hover:bg-muted/50',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {toDrafts ? (
        <p className={SETTINGS_ROW_DESCRIPTION}>
          Mora sends this to your TikTok inbox. Finish in TikTok, where you can pick a sound.
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            <span className={FIELD_LABEL}>Who can view this</span>
            <Select
              value={settings.privacyLevel ?? undefined}
              onValueChange={(next) => update({ privacyLevel: next as TikTokPrivacyLevel })}
              disabled={disabled}
            >
              <SelectTrigger aria-label="Who can view this" className="text-caption">
                <SelectValue placeholder="Choose who can see this" />
              </SelectTrigger>
              <SelectContent>
                {view.privacyOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabledReason !== null}
                  >
                    {option.label}
                    {option.disabledReason ? ` (${option.disabledReason})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <span className={FIELD_LABEL}>Interactions</span>
            {view.interactions.map((item) => (
              <CheckRow
                key={item.key}
                id={`tiktok-${item.key}`}
                label={item.label}
                checked={item.checked}
                disabled={disabled || item.disabled}
                note={item.disabledReason}
                onChange={(next) => update({ [item.key]: next })}
              />
            ))}
          </div>

          {mediaKind === 'photo' && photoCount > 1 ? (
            <div className="flex flex-col gap-1.5">
              <span className={FIELD_LABEL}>Cover photo</span>
              <Select
                value={String(Math.min(settings.photoCoverIndex, photoCount - 1))}
                onValueChange={(next) => update({ photoCoverIndex: Number(next) })}
                disabled={disabled}
              >
                <SelectTrigger aria-label="Cover photo" className="text-caption">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: photoCount }, (_, index) => (
                    <SelectItem key={index} value={String(index)}>
                      {`Photo ${index + 1}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {mediaKind === 'photo' ? (
            <Row
              title="Add recommended music"
              description="TikTok picks the track. You can change it in TikTok."
              control={
                <Switch
                  size="sm"
                  aria-label="Add recommended music"
                  checked={settings.autoAddMusic}
                  disabled={disabled}
                  onCheckedChange={(next) => update({ autoAddMusic: next })}
                />
              }
            />
          ) : null}

          <div className="flex flex-col gap-2">
            <Row
              title="Content disclosure"
              description="Turn on if this post promotes a brand, product or service."
              control={
                <Switch
                  size="sm"
                  aria-label="Content disclosure"
                  checked={settings.commercialContent}
                  disabled={disabled}
                  onCheckedChange={(next) =>
                    update(
                      next
                        ? { commercialContent: true }
                        : { commercialContent: false, yourBrand: false, brandedContent: false },
                    )
                  }
                />
              }
            />
            {settings.commercialContent ? (
              <div className="ml-1 flex flex-col gap-2 border-l border-border/60 pl-3">
                <CheckRow
                  id="tiktok-your-brand"
                  label="Your brand"
                  note="You are promoting yourself or your own business."
                  checked={settings.yourBrand}
                  disabled={disabled}
                  onChange={(next) => update({ yourBrand: next })}
                />
                <CheckRow
                  id="tiktok-branded-content"
                  label="Branded content"
                  note={
                    view.brandedContentDisabledReason ??
                    'You are promoting another brand or a third party.'
                  }
                  checked={settings.brandedContent}
                  disabled={disabled || view.brandedContentDisabledReason !== null}
                  onChange={(next) => update({ brandedContent: next })}
                />
                {view.commercialLabel ? (
                  <p className={SETTINGS_ROW_DESCRIPTION}>{view.commercialLabel}</p>
                ) : null}
                {view.holdReason ? (
                  <p className="text-detail text-destructive" role="alert">
                    {view.holdReason}
                  </p>
                ) : null}
                {settings.brandedContent && settings.privacyLevel === 'SELF_ONLY' ? (
                  <p className="text-detail text-destructive" role="alert">
                    {TIKTOK_BRANDED_PRIVATE_REASON}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          <CheckRow
            id="tiktok-consent"
            label={view.consentText}
            note="Processing can take a few minutes, so the post may not show on your profile straight away."
            checked={settings.consent}
            disabled={disabled}
            onChange={(next) => update({ consent: next })}
          />
        </>
      )}
    </section>
  );
}
